import React, { useMemo } from "react";

import type { AccountData } from "../../types/generalUserManage";

import AccountDashboardBox from "./AccountDashboardBox";

interface AccountDashboardProps {
  accountData: AccountData | null;
  totalUserCreditLimit: number;
  totalUserCreditConsumed: number;
  displayPeriod: string;
  onDisplayPeriodChange: (period: string) => void; // eslint-disable-line no-unused-vars
}

const AccountDashboard: React.FC<AccountDashboardProps> = ({
  accountData,
  totalUserCreditLimit,
  totalUserCreditConsumed,
  displayPeriod,
  onDisplayPeriodChange,
}) => {
  // 表示期間のリストを生成（契約開始月から現在月まで、降順）
  const periodOptions = useMemo(() => {
    const contractStartDate = new Date(accountData.contract_start_date);
    const now = new Date();
    const options: { value: string; label: string }[] = [];

    // 現在の年月から開始（降順なので）
    let currentYear = now.getFullYear();
    let currentMonth = now.getMonth() + 1; // getMonth()は0-11なので+1

    const contractStartYear = contractStartDate.getFullYear();
    const contractStartMonth = contractStartDate.getMonth() + 1;

    // 契約開始年月まで遡る
    while (
      currentYear > contractStartYear ||
      (currentYear === contractStartYear && currentMonth >= contractStartMonth)
    ) {
      const value = `${currentYear}-${String(currentMonth).padStart(2, "0")}`;
      const label = `${currentYear}年${String(currentMonth).padStart(2, "0")}月`;
      options.push({ value, label });

      // 前月へ
      currentMonth -= 1;
      if (currentMonth === 0) {
        currentMonth = 12;
        currentYear -= 1;
      }
    }

    return options;
  }, [accountData.contract_start_date]);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">ダッシュボード</h2>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
            表示期間
          </span>
          <select
            value={displayPeriod}
            onChange={(e) => onDisplayPeriodChange(e.target.value)}
            className="block p-2 text-gray-900 border border-gray-300 rounded bg-gray-50 text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            {periodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-6 m-4">
        <div className="space-y-2">
          <div className="flex">
            <span className="text-2xl font-bold">
              {accountData.corporate_name || "-"}
            </span>
          </div>
          <div className="flex">
            <span className="font-medium text-gray-600 text-lg w-48">
              契約プラン:&nbsp;
            </span>
            <span className="text-gray-900 text-lg">
              {accountData.plan_type?.name || "-"}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <AccountDashboardBox
              title="全体使用済み"
              mainText={totalUserCreditConsumed}
              subText={`/ ${accountData.credit_limit}クレジット`}
            />
            <AccountDashboardBox
              title="残りクレジット"
              mainText={accountData.credit_limit - totalUserCreditConsumed}
              subText="利用可能"
            />
            <AccountDashboardBox
              title="割振済みクレジット"
              mainText={totalUserCreditLimit}
              subText={`/ ${accountData.credit_limit}クレジット`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDashboard;
